/**
 * Wrap admin-only pages so non-admin viewers never see them rendering.
 *
 * Three states:
 *   - auth still loading → blank with a spinner (no flash of content)
 *   - authed but not admin → redirect to /prompts (their landing page)
 *   - admin → render children
 *
 * Defence in depth: the backend already 403s the underlying endpoints
 * for non-admin, but this guard means non-admin never even sees the
 * page chrome / partial content.
 */
import { Navigate } from '@tanstack/react-router';

import { PageLoading } from '@/components/Spinner';
import { useAuthMe } from '@/lib/auth';

export function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, isLoading } = useAuthMe();
  if (isLoading) return <PageLoading />;
  // Open mode (no auth configured on the box) treats everyone as
  // anonymous admin; tests + dev rely on that, so don't redirect.
  if (!user || user.email === 'anonymous') return <>{children}</>;
  if (!isAdmin) return <Navigate to="/prompts" />;
  return <>{children}</>;
}
